<template>
  <v-hover>
    <base-card
      slot-scope="{ hover }"
      color="secondary"
      class="tk-artwork"
      :class="{ 'elevation-12 scale-down': hover }"
    >
      <component
        :is="clickable ? 'router-link' : 'div'"
        :to="{ path: artworkPath }"
        :aria-label="artwork.title"
        :target="hrefTarget"
      >
        <tk-media
          contain
          min-height="50px"
          position="top center"
          sizes="350px"
          lazy-src="/static/img/placeholder.png"
          :class="{
            inactive:
              artwork.is_active === false || artwork.is_visible === false,
          }"
          :media="artwork.media"
        >
          <div
            v-if="!artwork.isForSale"
            class="sold-ribbon"
          >
            <span>{{ $t('COMMON.SOLD') }}</span>
          </div>

          <tk-btn
            aria-label="delete"
            v-if="removeBtn"
            icon
            small
            class="close-btn"
            @click="onRemove"
          >
            <v-icon size="18">fas fa-trash</v-icon>
          </tk-btn>

          <div class="text-xs-left">
            <div class="media-count ml-2">
              <tk-btn
                small
                v-if="artwork.mediaCount >= 2"
                class="ml-0"
              >
                <v-icon
                  dark
                  left
                  size="18"
                  class="pa-0 mr-1"
                >fas fa-camera
                </v-icon
                >
                <span>{{ artwork.mediaCount }}</span>
              </tk-btn>
              <tk-btn
                small
                class="mx-0"
                v-if="artwork.video_id"
              >
                <v-icon
                  dark
                  left
                  size="18"
                  class="pa-0 ma-0"
                >fas fa-video
                </v-icon
                >
              </tk-btn>
            </div>
          </div>
        </tk-media>
      </component>

      <v-card-title
        primary-title
        class="pa-3"
      >
        <v-container
          px-0
          py-0
        >
          <!-- If artwork is locked -->
          <!-- Show locked period only for artist and admin -->
          <v-layout
            row
            align-center
            v-if="
              artwork.locked_until &&
              ($auth.currentUser.isArtist || $auth.currentUser.isAdmin)
            "
          >
            <v-flex
              shrink
              pr-0
            >
              <v-icon
                class="artwork-title-icon mr-1"
                size="12"
              >fas fa-lock
              </v-icon
              >
            </v-flex>
            <v-flex
              pa-0
              grow
            >
              <div class="artwork-title">
                <date-display
                  v-if="artwork.locked_until"
                  :date="artwork.locked_until"
                />
              </div>
            </v-flex>
          </v-layout>
          <!-- /If artwork is locked -->

          <!-- Artist name and price layout -->
          <v-layout
            row
            align-center
            justify-center
          >
            <v-flex
              v-if="artwork.artist"
              pb-0
              pt-1
              class="ellipis"
              shrink
            >
              <h2
                v-if="artwork.artist.isVisible === false"
                class="artwork-title ellipis"
              >
                {{ artwork.artist.name }}
              </h2>

              <component
                :is="clickable ? 'router-link' : 'div'"
                v-else
                class="artwork-title ellipis"
                :to="{
                  name: 'artist.single',
                  params: { artistSlug: artwork.artist.slug },
                }"
                :target="hrefTarget"
              >{{ artwork.artist.name }}
              </component
              >
            </v-flex>
            <v-flex
              pb-0
              pt-1
              grow
              text-xs-right
            >
              <component
                :is="clickable ? 'router-link' : 'div'"
                :to="{ path: artworkPath }"
                :aria-label="artwork.title"
                :target="hrefTarget"
                class="title no-decoration"
              >{{ artwork.price }} €
              </component
              >
            </v-flex>
          </v-layout>

          <!-- Artwork title and dimention layout -->
          <v-layout
            row
            align-center
            justify-center
          >
            <v-flex
              pt-1
              shrink
              mr-2
              class="ellipis"
            >
              <div class="font-weight-light ellipis artwork-title">
                {{ artwork.title }}
              </div>
            </v-flex>
            <v-flex
              pt-1
              grow
              text-xs-right
            >
              <component
                :is="clickable ? 'router-link' : 'div'"
                v-if="artwork.dimension"
                :to="{ path: artworkPath }"
                :aria-label="artwork.title"
                :target="hrefTarget"
                class="no-decoration"
              >
                <div class="font-weight-light artwork-dimension">
                  {{ artwork.dimension }}
                </div>
              </component>
            </v-flex>
          </v-layout>
        </v-container>
      </v-card-title>

      <slot name="action-btns"></slot>
    </base-card>
  </v-hover>
</template>

<script>
import DateDisplay from '@/components/base/DateDisplay'

export default {
  components: { DateDisplay },
  props: {
    artwork: { required: true },
    showArtistName: { default: false },
    removeBtn: { default: false },
    clickable: { default: true },
  },
  name: 'TkArtwork',

  computed: {
    artworkPath () {
      if (this.artwork.isTestable && !this.$vuetify.breakpoint.xsOnly) {
        return this.artwork.path + '/test/default/'
      }
      return this.artwork.path
    },
    // Open in new tab if the listing is from search page
    hrefTarget () {
      // donot open artwork in new tab during development.
      if (process.env.VUE_APP_ENV === 'development') return null
      return this.$route.name === 'search' ? '_blank' : null
    },
  },

  created () {
    // console.log("TkArtwork", this.artwork);
  },

  methods: {
    onRemove (e) {
      e.preventDefault()
      this.$emit('remove', this.artwork)
    },
  },
}
</script>

<style
  lang="scss"
  scoped
>
.inactive {
  &:after {
    content: "";
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.75);
  }
}

.close-btn {
  position: absolute;
  right: 0;
  top: 0;
  opacity: 0.7;
  background-color: rgba(255, 255, 255, 0.3);
}

.media-count {
  position: absolute;
  bottom: 0px;
  opacity: 0.6;

  .v-btn {
    min-width: 40px;
    border-radius: 6px;

    span {
      margin-top: 3px;
      font-weight: 600;
    }
  }
}

.v-image {
  background-color: white;
}

.artwork-title {
  font-size: 16px;

  &:hover {
    text-decoration: none;
  }
}

.no-decoration {
  text-decoration: none;

  &:hover {
    text-decoration: none;
  }
}

@media (min-width: 960px) {
  .title {
    font-size: 16px !important;
  }
  .artwork-title {
    font-size: 14px !important;

    &-icon {
      font-size: 12px !important;
      position: relative;
      top: -2px;
    }
  }
  .artwork-dimension {
    font-size: 10px !important;
  }
}
</style>


